import { FC, ReactNode, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router';
import { Link } from 'react-router-dom';

import {
  useGetCurrentLoginInformationsQuery,
  useGetCurrentUserDetailsQuery,
  useGetUserConfigQuery,
  useGetUserProfilePictureQuery,
} from 'apis/user.api';
import { ReactComponent as DarkLogoSvg } from 'assets/svg/easy-dark.svg';
import { ReactComponent as LightLogoSvg } from 'assets/svg/easy-light.svg';
import { ReactComponent as BigLightLogoSvg } from 'assets/svg/logo-full-dark.svg';
import { ReactComponent as BigDarkLogoSvg } from 'assets/svg/logo-full-light.svg';
import { HOME_PAGE_YEAR } from 'constants/home';
import useImpersonateUser from 'hooks/useImpersonateUser';
import { useOpen } from 'hooks/useOpen';
import useSubscriptionInformation from 'hooks/useSubscriptionInformation';
import useTheme from 'hooks/useTheme';
import { CommonError } from 'interfaces/shared.interface';
import { ThemeConfig } from 'interfaces/user.interface';
import { useAppSelector } from 'modules/store';
import { selectTenantId } from 'modules/user/selector';
import ProfileSettingsModalContent from 'page-components/profile-settings/ProfileSettingsModalContent';
import { Header, SettingsDropdown, Modal, Sidebar, LoadingOverlay, ErrorModal } from 'shared-components';
import { cn } from 'utils/global';

interface Props {
  children?: ReactNode;
  childrenClassName?: string;
  pageTitle: string | undefined;
}

const ApplicationLayout: FC<Props> = ({ childrenClassName, pageTitle }) => {
  const { t } = useTranslation();
  const [isSidebarVisible, setSidebarVisible] = useState(true);
  const [nestedModalIsOpened, setNestedModalIsOpened] = useState(true);
  const [isOpenSettingsModal, handleOpenSettingsModal, handleCloseSettingsModal] = useOpen();
  const [isErrorModalOpen, handleOpenErrorModal, handleOnCloseErrorModal] = useOpen();
  const { theme } = useTheme();

  const { handleImpersonator, isFetching } = useImpersonateUser({ onError: handleOpenErrorModal });

  const {
    data: currentUserDetails,
    error: currentUserError,
    isLoading: isCurrentUserLoading,
  } = useGetCurrentUserDetailsQuery();

  const {
    isLoading,
    isSubscription,
    isInTrialPeriod,
    displayName,
    subscriptionId,
    subscriptionAwaitingCancellation,
    isSubscriptionDeactivated,
    subscriptionEndDateUtc,
    expiringSubscriptionName,
  } = useSubscriptionInformation();

  const { data, error: currentLoginInfoError } = useGetCurrentLoginInformationsQuery();
  const { name, surname, emailAddress } = data?.user || {};

  useGetUserConfigQuery();

  const tenantId = useAppSelector(selectTenantId) || 0;

  const {
    data: pictureData,
    isLoading: isProfilePictureLoading,
    error: userProfilePictureError,
  } = useGetUserProfilePictureQuery(
    { profilePictureId: currentUserDetails?.profilePictureId || '', tenantId: tenantId || 0 },
    {
      skip: !currentUserDetails?.profilePictureId,
    }
  );

  const errorCurentUserData = (currentUserError as CommonError)?.data || {};

  const errorCurrentLoginInfoData = (currentLoginInfoError as CommonError)?.data || {};

  const errorUserProfilePictureErrorData = (userProfilePictureError as CommonError)?.data || {};

  const errorData = errorCurentUserData || errorCurrentLoginInfoData || errorUserProfilePictureErrorData;

  const toggleSidebar = () => {
    setSidebarVisible((prevState) => !prevState);
  };

  const handleNestedModalIsOpenedChange = (isNested: boolean) => setNestedModalIsOpened(isNested);

  const logo = isSidebarVisible ? (
    theme === ThemeConfig.dark ? (
      <BigDarkLogoSvg />
    ) : (
      <BigLightLogoSvg />
    )
  ) : theme === ThemeConfig.light ? (
    <LightLogoSvg />
  ) : (
    <DarkLogoSvg />
  );

  return (
    <div className="flex relative">
      {isSidebarVisible && (
        <div
          className="fixed block lg:hidden inset-0 bg-black bg-opacity-30 lg:bg-opacity-0 z-20"
          onClick={() => setSidebarVisible(false)}
        />
      )}
      <div
        data-cy="sidebar-block"
        className={cn(
          'sidebar fixed flex-shrink-0 overflow-y-auto bg-white-00 dark:bg-black-900 transition-width duration-200 h-screen flex flex-col items-center border-r-1 border-neutral-200 dark:border-neutral-800 z-30',
          {
            'w-64': isSidebarVisible,
            'w-20': !isSidebarVisible,
            'hidden lg:block': !isSidebarVisible,
          }
        )}
      >
        <div className="flex flex-col flex-grow overflow-y-auto items-center w-full">
          <div
            className={cn('h-14 w-full flex items-center justify-center mb-6', {
              'px-6 py-4': isSidebarVisible,
            })}
          >
            <a href="/">{logo}</a>
          </div>
          {data ? <Sidebar isSidebarVisible={isSidebarVisible} userType={data?.tenant ? 'tenant' : 'admin'} /> : ''}
        </div>

        <div
          className={cn(
            'absolute bottom-0 left-0 right-0 bg-white-00 dark:bg-black-900 flex justify-center text-white text-xs py-3.5',
            { 'lg:flex': isSidebarVisible, hidden: !isSidebarVisible }
          )}
        >
          <b className="mr-0.5 text-neutral-400">
            {t('Copyright')}. &copy; {HOME_PAGE_YEAR}
          </b>
          <Link to="#" className="text-primary-600">
            {t('EasyGo')}.
          </Link>
        </div>
      </div>

      <Header
        pictureData={pictureData}
        isProfilePictureLoading={isProfilePictureLoading || isCurrentUserLoading}
        isSidebarVisible={isSidebarVisible}
        onClick={toggleSidebar}
        isLoading={isLoading}
        isSubscription={isSubscription}
        isInTrialPeriod={isInTrialPeriod}
        displayName={displayName}
        tenantId={tenantId}
        subscriptionId={subscriptionId}
        subscriptionEndDateUtc={subscriptionEndDateUtc}
        subscriptionAwaitingCancellation={subscriptionAwaitingCancellation}
        isSubscriptionDeactivated={isSubscriptionDeactivated}
        expiringSubscriptionName={expiringSubscriptionName}
        pageTitle={pageTitle}
        firstName={name}
        lastName={surname}
        email={emailAddress}
      >
        <SettingsDropdown
          firstName={name}
          lastName={surname}
          handleOpenSettingsModal={handleOpenSettingsModal}
          className="animate-padding"
          isImpersonatorUser={!!data?.impersonatorUser}
          handleBackToImpersonator={handleImpersonator}
        />
      </Header>
      <div
        className={cn('flex flex-col w-full duration-200', {
          'lg:ml-64': isSidebarVisible,
          'ml-0 lg:ml-20 duration-0': !isSidebarVisible,
        })}
      >
        <main className={cn('flex-grow pt-16', childrenClassName)}>
          <Outlet />
        </main>
      </div>
      <LoadingOverlay isLoading={isFetching} isFixed />
      <ErrorModal
        errorMessage={errorData?.error?.message}
        description={errorData?.error?.details}
        isOpen={isErrorModalOpen}
        handleClose={handleOnCloseErrorModal}
      />
      <Modal
        heading={t('AccountSettings')}
        isOpen={isOpenSettingsModal}
        onClose={handleCloseSettingsModal}
        isDisableOutsideClick={nestedModalIsOpened}
      >
        <ProfileSettingsModalContent
          handleClose={handleCloseSettingsModal}
          handleNestedModalIsOpenedChange={handleNestedModalIsOpenedChange}
        />
      </Modal>
    </div>
  );
};

export default ApplicationLayout;
